/* eslint valid-jsdoc: 0 */

import Context from "shared/util/Context";
import $ from "jquery";
import App from "reflux/okmodal/components/App";
import C from "reflux/okmodal/util/Constants";
import OkModalManager from "reflux/okmodal/util/OkModalManager";
import ReactDOM from "react-dom";

// Log an error string to the console with a stylish OkModal prefix.
function logSimpleError(message) {
	if (!message || Context.get("env") === "prod") {
		return;
	}

	console.error("%cOkModal:", "font-weight: bold; font-style: italic;", message);
}

/**
 * Class that creates a new modal. Takes a config object, the properties of
 * which are outlined below. Note that while neither ctaLink or onAction are
 * required properties, one of the two must be specified for OkModal to work.
 *
 * @param [config.body]                {String}
 * @param [config.ctaLink]             {String}
 * @param [config.ctaText=Learn More]  {String}
 * @param [config.dismissText]         {String}
 * @param config.headline              {String}
 * @param [config.image]               {String}
 * @param [config.onAction=noop]     {Function}
 * @param [config.onClose=noop]      {Function}
 * @param [config.subheadline]         {String}
 * @param [config.windowshade=true]    {Boolean}
 *
 * @example
 * new OkModal({
 *   headline: "This is a test modal",
 *   onAction: (e) => {
 *     e.preventDefault();
 *     console.log("User clicked the call-to-action button.");
 *   }
 * })
 */
class OkModal {
	constructor(config) {
		if (!config.headline || (!config.ctaLink && !config.onAction)) {
			logSimpleError("Not enough information to initiate modal.");
			return;
		}

		this._active = true;
		this.config = {
			...C.DEFAULT_CONFIG,
			...config,
		};

		// Set the default target node.
		// This has to be done here instead of stored in constants becase the
		// node isn't on the page when Constants is pulled in.
		if (!this.config.target) {
			this.config.target = document.getElementById(C.CSS_PREFIX);
		}

		if (!this.config.target) {
			logSimpleError(`
				Couldn't find the target element. Pass an HTMLElement in the
				"target" prop."
			`);
			return;
		}

		this._modalID = `modal-${Date.now()}`;
		OkModalManager.add(this._modalID, this);
		this.open();
	}

	destructor() {
		// Make sure the destructor runs once and only once. This is here so devs
		// can indiscriminately call OkModalManager.closeAll().
		if (this._active === false) {
			return;
		}

		this._active = false;

		this.fadeOut(() => {
			this.config.onClose();

			// Remove the node from OkModalManager.
			OkModalManager.remove(this._modalID);

			if (this.config.noscroll) {
				document.body.className += document.body.className.replace(/ ?noscroll/g, "");
			}

			if (!this.config.noclose) {
				// Remove event listeners.
				this._modalNode.removeEventListener("click", (e) => {
					this._handleMouseClick(e);
				});
			}

			// Remove markup.
			this.config.target.removeChild(this._modalNode);

			// Null out variables to hint to the browser they're ready for GC.
			this.config = null;
			this._modalID = null;
			this._modalNode = null;
			this._active = null;
		});
	}

	/**
	 * It's usually better to run OkModalManager.closeAll() but if you do need to
	 * close a specific modal, use this method.
	 */
	close(force = false) {
		if (this.config.noclose && !force) {
			return;
		}

		this.destructor();
	}

	/**
	 * Fade the modal in then run a callback function.
	 *
	 * @param [callback] {Function}
	 */
	fadeIn(callback) {
		this._modalNode.className += " is-visible";
		setTimeout(callback, C.FADE_OUT_TIME);
	}

	/**
	 * Fade the modal out then run a callback function.
	 *
	 * @param [callback] {Function}
	 */
	fadeOut(callback) {
		this._modalNode.className = this._modalNode.className.replace(/ ?is-visible/, "");
		setTimeout(callback, C.FADE_OUT_TIME);
	}

	open() {
		this._modalNode = document.getElementById(this._modalID);

		if (!this._modalNode) {
			this._modalNode = document.createElement("div");
			this._modalNode.id = this._modalID;
			this._modalNode.className = `${C.CSS_PREFIX}-inner`;
			this.config.target.appendChild(this._modalNode);
		}

		ReactDOM.render(<App {...this.config} />, this._modalNode);
		setTimeout(() => {
			this._modalNode.className += " is-visible";
		}, 50);

		if (!this.config.noclose) {
			this._modalNode.addEventListener("click", (e) => {
				this._handleMouseClick(e);
			});
		}

		if (this.config.noscroll) {
			document.body.className += " noscroll";
		}
	}

	_handleMouseClick(e) {
		if (
			e.target.className.indexOf(`${C.CSS_PREFIX}-wrapper`) > -1 ||
			e.target.className.indexOf("FullscreenOverlay") > -1 ||
			e.target.parentNode.className.indexOf(`${C.CSS_PREFIX}Content-dismiss`) > -1
		) {
			this.close();
		}
	}
}

/**
 * The only time you'll need to run this method is if you do some dynamic
 * loading of markup into the site.
 *
 * Runs automatically on page load and finds HTMLElements with a predefined
 * markup pattern to initialize a modal. By default these modals don't have
 * the same behavior as ones that are invoked with the new constructor, these
 * typically don't have a windowshade and are positioned relative to where the
 * data-okmodal element is in your markup.
 *
 * @example
 * <div data-okmodal
 *      data-okmodal-headline="This is a modal"
 *      data-okmodal-ctaLink="https://okcupid.com/upgrade"></div>
 */
function OkModalInitializer() {
	$("[data-okmodal]").each((index, el) => {
		const headline = el.getAttribute("data-okmodal-headline");
		const ctaLink = el.getAttribute("data-okmodal-ctaLink");
		const windowshade = el.getAttribute("data-okmodal-windowshade") === "true";
		let target = el;

		if (!headline || !ctaLink) {
			return;
		}

		if (windowshade) {
			target = document.getElementById("#OkModal");
		}

		// default, always-passed props
		// ----------------------------

		const modalProps = {
			target,
			headline,
			ctaLink,
			windowshade,
			noclose: el.getAttribute("data-okmodal-noclose") === "true" || false,
			noscroll: el.getAttribute("data-okmodal-noscroll") === "true" || false,
			image: el.getAttribute("data-okmodal-image") || null,
			subheadline: el.getAttribute("data-okmodal-subheadline") || null,
			body: el.getAttribute("data-okmodal-body") || null,
			className: el.getAttribute("data-okmodal-className") || null,
		};


		// props to only pass if we have a setting present
		// -----------------------------------------------

		if (el.getAttribute("data-okmodal-ctaText")) {
			modalProps.ctaText = el.getAttribute("data-okmodal-ctaText");
		}

		if (el.getAttribute("data-okmodal-dismissText")) {
			modalProps.dismissText = el.getAttribute("data-okmodal-dismissText");
		}


		// initialize this tasty motherfucker
		// ----------------------------------

		new OkModal(modalProps);
	});
}

$(OkModalInitializer);

// For legacy JS, Jan. 4 2016. Once we can use require() everywhere this is no
// longer necessary.
window.OkModal = OkModal;

export default OkModal;



// WEBPACK FOOTER //
// ./src/reflux/okmodal/app.js